import React from 'react';
import { TamEstimationResult } from '../../hooks/useTamEstimation';

interface TamSuggestionProps {
  fieldName: string;
  label: string;
  tamValue: number;
  unit?: string;
  onAccept: () => void;
}

export const TamSuggestion: React.FC<TamSuggestionProps> = ({
  fieldName,
  label,
  tamValue,
  unit = '',
  onAccept
}) => {
  const formatValue = (value: number) => {
    // Format based on field type - check specific fields first, then patterns
    if (fieldName === 'annualGallons' || fieldName.includes('Volume') || fieldName.includes('gallons')) {
      // Large numbers (gallons) - no currency symbol
      return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
    } else if (fieldName === 'fuelMargin' || fieldName.includes('profit_margin_per_gal')) {
      // Currency per gallon (e.g., $0.51)
      return `$${value.toFixed(2)}`;
    } else if (fieldName === 'storeMargin' || fieldName.includes('cstore_margin')) {
      // Percentage (e.g., 39.7%)
      return `${value.toFixed(1)}%`;
    } else if (fieldName.includes('Margin') || fieldName.includes('margin')) {
      // Other margins as percentage
      return `${value.toFixed(1)}%`;
    } else {
      // Regular currency (e.g., $1,500,000)
      return `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    }
  };

  return (
    <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-1">
          <span className="text-sm text-blue-700">
            <span className="font-medium">💡 AI Projection:</span>{' '}
            <strong className="text-blue-900">{formatValue(tamValue)}{unit}</strong>
          </span>
        </div>
        <button
          type="button"
          onClick={onAccept}
          className="ml-3 text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 transition-colors font-medium whitespace-nowrap"
        >
          Use This Value
        </button>
      </div>
      <div className="text-xs text-blue-600 mt-1.5 flex items-center gap-1">
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Based on operational comparables and market data. You can enter your own value or click "Use This Value".
      </div>
    </div>
  );
};

interface TamDiagnosticsProps {
  result: TamEstimationResult;
}

export const TamDiagnostics: React.FC<TamDiagnosticsProps> = ({ result }) => {
  return (
    <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg text-xs space-y-2">
      <div className="font-semibold text-gray-700 mb-2">Projection Details</div>

      {/* Cohort Info */}
      <div className="flex justify-between">
        <span className="text-gray-600">Data Source:</span>
        <span className="font-medium text-gray-900">
          {result.diagnostics.cohort.tier === 'county' ? 'County' :
           result.diagnostics.cohort.tier === 'state' ? 'State' : 'National'}
          {' '}({result.diagnostics.cohort.n} comparable stations)
        </span>
      </div>

      {/* Brand Factor */}
      {result.diagnostics.factors.brand.n > 0 && (
        <div className="flex justify-between">
          <span className="text-gray-600">Brand Adjustment:</span>
          <span className="font-medium text-gray-900">
            {(result.diagnostics.factors.brand.factor * 100).toFixed(0)}%
            {' '}(n={result.diagnostics.factors.brand.n})
          </span>
        </div>
      )}

      {/* Geography Factor */}
      {result.diagnostics.factors.geography.n > 0 && (
        <div className="flex justify-between">
          <span className="text-gray-600">Market Adjustment:</span>
          <span className="font-medium text-gray-900">
            {(result.diagnostics.factors.geography.factor * 100).toFixed(0)}%
            {' '}(n={result.diagnostics.factors.geography.n})
          </span>
        </div>
      )}

      {/* C-Store Method */}
      <div className="flex justify-between">
        <span className="text-gray-600">C-Store Method:</span>
        <span className="font-medium text-gray-900 capitalize">
          {result.diagnostics.cstore_link.method.replace('_', ' ')}
          {result.diagnostics.cstore_link.r2 !== null &&
            ` (R²=${result.diagnostics.cstore_link.r2.toFixed(2)})`}
        </span>
      </div>

      {/* Confidence Indicator */}
      <div className="mt-3 pt-2 border-t border-gray-300">
        <div className="flex items-center gap-2">
          <span className="text-gray-600">Confidence:</span>
          <div className="flex-1 flex items-center gap-2">
            {result.diagnostics.cohort.tier === 'county' && result.diagnostics.cohort.n >= 30 ? (
              <>
                <div className="flex-1 bg-green-200 h-2 rounded-full">
                  <div className="bg-green-600 h-2 rounded-full" style={{width: '90%'}}></div>
                </div>
                <span className="text-green-700 font-medium">High</span>
              </>
            ) : result.diagnostics.cohort.tier === 'state' || result.diagnostics.cohort.n >= 15 ? (
              <>
                <div className="flex-1 bg-yellow-200 h-2 rounded-full">
                  <div className="bg-yellow-600 h-2 rounded-full" style={{width: '60%'}}></div>
                </div>
                <span className="text-yellow-700 font-medium">Medium</span>
              </>
            ) : (
              <>
                <div className="flex-1 bg-orange-200 h-2 rounded-full">
                  <div className="bg-orange-600 h-2 rounded-full" style={{width: '40%'}}></div>
                </div>
                <span className="text-orange-700 font-medium">Low</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
